import React, { useEffect, useState } from "react";
import { Box, Paper, Tabs, Tab } from "@mui/material";
import { useSelector } from "react-redux";
import type { RootState } from "../../store/Index";
import { memorialTabs } from "./MemorialTabs";
import { TabPanel, a11yProps } from "./components/TabPanel";
import HeaderCard from "./components/HeaderCard";
import SideBar from "./components/SideBar";
import FeaturesPanel from "./panels/Features";
import LivestreamPanel from "./panels/Livestream";
import ObituaryPanel from "./panels/Orbituary";
import TimelinePanel from "./panels/TimelinePanel";
import ChemaPanel from "./panels/Chema";
import FavoritesPanel from "./panels/FavoritesPanel";
import MemoriesPanel from "./panels/Memories";
import GalleryPanel from "./panels/Gallery";
import type { FeatureFlags } from "./MemorialTypes";
import { useNavigate, useParams } from "react-router-dom";
import { BookingResponse } from "../bookings/types/view-booking";
import apiService from "../../service/Api";
import { toast } from "react-toastify";

const ViewMemorialPage: React.FC = () => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [tabValue, setTabValue] = useState(0);
  const [featuresEnabled, setFeaturesEnabled] = useState<FeatureFlags>(() => {
    const initial: FeatureFlags = {};
    memorialTabs.forEach((tab) => {
      initial[tab.id] = true;
    });
    return initial;
  });

  const handleTabChange = (_e: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const toggleFeature = (tabId: string) => {
    setFeaturesEnabled((prev) => ({ ...prev, [tabId]: !prev[tabId] }));
  };
  const favoriteItems = React.useMemo(
    () =>
      Object.keys(featuresEnabled).map((id) => ({
        id,
        title: id,
        description: "",
        enabled: !!featuresEnabled[id],
      })),
    [featuresEnabled]
  );

  const { id } = useParams<{ id: string }>();
  const [bookingData, setBookingData] = useState<BookingResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBookings = async () => {
      if (!id) {
        setError("Invalid booking ID");
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        // Fetch booking data
        const bookingResponse = await apiService.get<BookingResponse>(
          `/api/bookings/booking/${id}/?source_of_booking=client_portal`
        );

        setBookingData(bookingResponse.data);

        setError(null);
      } catch (err: any) {
        let errorMessage =
          "Failed to load booking or streams. Please try again.";
        if (err.response?.status === 500) {
          errorMessage =
            "Server error: Unable to retrieve data. Please try again later or contact support.";
        } else if (err.response?.status === 401) {
          errorMessage = "Unauthorized: Please log in again.";
          navigate("/login");
        } else if (err.response?.status === 403) {
          errorMessage =
            "Permission denied: You do not have access to this booking.";
          navigate("/bookings");
        } else if (err.response?.data?.detail) {
          errorMessage = err.response.data.detail;
        }
        setError(errorMessage);
        toast.error(errorMessage, {
          position: "top-right",
          autoClose: 5000,
          theme: "light",
        });
        console.error("Fetch error:", err.response?.data, err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [id, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "stretch", // ✅ added → makes both columns same height
        gap: 2, // ✅ added → replaces mr: 2 spacing
        minHeight: "100vh",
        maxWidth: "100vh",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%",
          alignItems: "stretch", // ✅ added → ensures HeaderCard and TabPanel stretch
        }}
      >
        <HeaderCard reduxTheme={reduxTheme} bookingData={bookingData} />

        <Paper
          sx={{
            p: 2,
            boxShadow: 0,
            borderRadius: 3,
            flex: 1,
          }}
        >
          <Box
            sx={{
              width: "100%",
            }}
          >
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                aria-label="memorial tabs"
                sx={{ "& .MuiTab-root": { textTransform: "none" } }}
              >
                {memorialTabs.map((tab, index) => (
                  <Tab key={tab.id} label={tab.label} {...a11yProps(index)} />
                ))}
              </Tabs>
            </Box>

            {memorialTabs.map((tab, index) => (
              <TabPanel key={tab.id} value={tabValue} index={index}>
                {tab.id === "features" ? (
                  <FeaturesPanel
                    reduxTheme={reduxTheme}
                    deceasedId={bookingData?.deceased?.[0]?.id || null}
                    featuresEnabled={featuresEnabled}
                    toggleFeature={toggleFeature}
                  />
                ) : tab.id === "livestream" ? (
                  <LivestreamPanel reduxTheme={reduxTheme} />
                ) : tab.id === "obituary" ? (
                  <ObituaryPanel
                    reduxTheme={reduxTheme}
                    deceasedId={bookingData?.deceased?.[0]?.id || null}
                    featuresEnabled={featuresEnabled}
                    toggleFeature={toggleFeature}
                  />
                ) : tab.id === "timeline" ? (
                  <TimelinePanel
                    reduxTheme={reduxTheme}
                    deceasedId={bookingData?.deceased?.[0]?.id || null}
                    featuresEnabled={featuresEnabled}
                    toggleFeature={toggleFeature}
                  />
                ) : tab.id === "chema" ? (
                  <ChemaPanel reduxTheme={reduxTheme} />
                ) : tab.id === "favorites" ? (
                  <FavoritesPanel
                    reduxTheme={reduxTheme}
                    deceasedId={bookingData?.deceased[0].id}
                  />
                ) : tab.id === "memories" ? (
                  <MemoriesPanel
                    reduxTheme={reduxTheme}
                    deceasedId={bookingData?.deceased?.[0]?.id}
                    bookingId={bookingData?.id} // <-- use bookingData.id here
                  />
                ) : tab.id === "gallery" ? (
                  <GalleryPanel
                    reduxTheme={reduxTheme}
                    deceasedId={bookingData?.deceased[0].id}
                  />
                ) : null}
              </TabPanel>
            ))}
          </Box>
        </Paper>
      </Box>

      <Box
        sx={{
          width: 320,
          flexShrink: 0, // ✅ replaced flex: "0 0 320px"
          display: "flex", // ✅ added → for stretch behavior
          flexDirection: "column", // ✅ added
          alignItems: "stretch", // ✅ added → ensures Sidebar fills height
          boxShadow: 0,
        }}
      >
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
            boxShadow: 0,
          }}
        >
          <SideBar
            reduxTheme={reduxTheme}
            bookingData={bookingData}
            deceased_id={bookingData?.deceased?.[0]?.id?.toString() || ""}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default ViewMemorialPage;
